// src/lib/docx-export.ts
// DOCX writer that mirrors the structure expected by our DOCX importer.
// We avoid docx numbering APIs and styling that can split runs.
// Output is plain paragraphs with exact markers the importer expects.

export type CueNote = {
  id: string;
  title: string;
  body_markdown: string;
  flags?: string[];
  source?: string;
};

export type PacketLike = {
  name: string;
  notes: CueNote[];
};

const BRAND_PREFIX = "CueBoard:";   // Importer expects lines like "CueBoard: <Title>"
const INDENT_PER_LEVEL = 2;         // Importer treats two spaces as one indent level

function toLines(markdown: string): string[] {
  const txt = (markdown ?? "").replace(/\r\n?/g, "\n");
  return txt.split("\n");
}

// Detect leading spaces + bullet markers; return normalized "- " line with exact indentation
function normalizeBulletLine(raw: string): string {
  const m = raw.match(/^(\s*)(.*)$/);
  const lead = (m?.[1] ?? "").replace(/\t/g, "  ");
  const rest = (m?.[2] ?? "");
  const level = Math.floor(lead.length / INDENT_PER_LEVEL);

  // Recognize common bullet markers, then normalize to "- "
  const mm = rest.match(/^(?:-\s+|\*\s+|\d+\)\s+|\d+\.\s+)?(.*)$/);
  const content = (mm?.[1] ?? "").trim();

  const indent = " ".repeat(level * INDENT_PER_LEVEL);
  return content ? `${indent}- ${content}` : "";
}

export async function exportPacketToDocx(packet: PacketLike): Promise<Blob> {
  // Dynamic import to avoid TS type quirks and keep bundlers happy
  const { Document, Packer, Paragraph, TextRun } = (await import("docx")) as any;

  const children: any[] = [];

  packet.notes.forEach((note, noteIndex) => {
    const title = (note.title ?? "Untitled").trim();

    // Header line — PLAIN (no bold, no heading style), single run:
    // "CueBoard: <Title>"
    children.push(
      new Paragraph({
        children: [new TextRun({ text: `${BRAND_PREFIX} ${title}` })],
      })
    );

    // "Notes:" line — PLAIN, single run
    children.push(
      new Paragraph({
        children: [new TextRun({ text: "Notes:" })],
      })
    );

    // Body: preserve blank lines; write bullet text with indentation directly
    const lines = toLines(note.body_markdown);
    lines.forEach((raw) => {
      if (!raw || raw.trim().length === 0) {
        children.push(new Paragraph({ children: [new TextRun({ text: "" })] }));
        return;
      }
      const line = normalizeBulletLine(raw);
      children.push(new Paragraph({ children: [new TextRun({ text: line })] }));
    });

    // Spacer between notes
    if (noteIndex < packet.notes.length - 1) {
      children.push(new Paragraph({ children: [new TextRun({ text: "" })] }));
    }
  });

  const doc = new Document({ sections: [{ children }] });
  return Packer.toBlob(doc);
}

// Convenience browser download helper
export async function downloadPacketAsDocx(packet: PacketLike): Promise<void> {
  const blob = await exportPacketToDocx(packet);
  const fnameStem =
    (packet.name || "CueBoard Prep Kit").replace(/[\\\/:*?"<>|]+/g, "").trim() || "CueBoard Prep Kit";
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = `${fnameStem}.docx`;
  document.body.appendChild(a);
  a.click();
  a.remove();
  URL.revokeObjectURL(url);
}
